import { ArrowUp, History, ImagePlus, Mic, PanelRight, Plus, Sparkles, Square } from 'lucide-react'
import { toast } from 'sonner'

import { KeyboardEvent, useCallback, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { resolveMediaUrl } from '@/lib/media/resolveUrl'
import { AssistantModel, assistantError } from '@/http/models/assistant/Assistant'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'

import { assistantText } from './assistantText'
import { useAssistant } from './AssistantProvider'
import { useVoiceInput } from './useVoiceInput'
import { ImageActions } from './ImageActions'

/** The assistant's mark: a sparkle in the assistant's violet. */
export const AssistantMark = ({ className }: { className?: string }) => (
  <span className={cn('grid size-7 shrink-0 place-items-center rounded-full bg-violet-lighter text-violet-dark', className)}>
    <Sparkles className="size-4" />
  </span>
)

const ImageDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { language } = useAssistant()
  const t = assistantText(language)
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [image, setImage] = useState<{ media_id: string; path: string; name: string } | null>(null)

  const generate = async () => {
    if (prompt.trim().length < 5) return
    setBusy(true)
    const res = await AssistantModel.generateImage(prompt.trim(), language)
    setBusy(false)
    if (res.success && res.data) {
      setImage(res.data.result)
      toast.success(language === 'bn' ? 'ছবিটি Files › AI images-এ সেভ হয়েছে' : 'Saved to Files › AI images')
    } else {
      toast.error(assistantError(res.error, t.errorGeneric))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setImage(null); setPrompt('') } }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t.generateImage}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t.imagePrompt} rows={3} maxLength={800} />
          {busy && <div className="grid aspect-square place-items-center rounded-lg bg-subdued"><Spinner className="size-6 text-muted-foreground" /></div>}
          {image && !busy && (
            <div className="assistant-fade-in space-y-2">
              <img src={resolveMediaUrl(image.path)} alt={image.name} className="w-full rounded-lg border border-border-subtle" />
              <ImageActions name={image.name} path={image.path} mediaId={image.media_id} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t.close}</Button>
          <Button onClick={generate} disabled={busy || prompt.trim().length < 5}>{t.generate}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface AssistantComposerProps {
  variant: 'floating' | 'home' | 'panel'
  onShowRecents?: () => void
  autoFocus?: boolean
}

/** The input: floating pill on every page, the large box on Home, and the panel's footer. */
export const AssistantComposer = ({ variant, onShowRecents, autoFocus }: AssistantComposerProps) => {
  const { language, capabilities, isBusy, isOpen, send, stop, open, close } = useAssistant()
  const t = assistantText(language)
  const [text, setText] = useState('')
  const [imageOpen, setImageOpen] = useState(false)
  const fromVoice = useRef(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const onVoiceText = useCallback((spoken: string) => {
    fromVoice.current = true
    setText((current) => (current ? `${current} ${spoken}` : spoken))
    inputRef.current?.focus()
  }, [])
  const voice = useVoiceInput(language, onVoiceText)

  const submit = () => {
    if (!text.trim() || isBusy) return
    send(text, fromVoice.current ? 'voice' : 'message')
    fromVoice.current = false
    setText('')
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  const statusText = voice.state === 'recording' ? t.listening : voice.state === 'transcribing' ? t.transcribing : null
  const floating = variant === 'floating'

  return (
    <div
      className={cn(
        'border border-border bg-card',
        floating && 'flex w-full items-center gap-2 rounded-full py-1.5 pl-2 pr-1.5 shadow-dropdown',
        variant === 'home' && 'rounded-2xl p-4 shadow-card',
        variant === 'panel' && 'rounded-2xl p-4 shadow-card',
      )}
    >
      {floating && <AssistantMark />}
      <div className={cn('min-w-0', floating ? 'flex-1' : 'w-full')}>
        <textarea
          ref={inputRef}
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={statusText ?? t.placeholder}
          aria-label={t.placeholder}
          rows={floating ? 1 : 2}
          maxLength={4000}
          className={cn(
            'w-full resize-none bg-transparent text-body1 outline-none placeholder:text-placeholder-foreground',
            floating ? 'max-h-24 py-1' : 'max-h-40 min-h-[2.5rem] py-1',
            language === 'bn' && 'font-bn',
          )}
        />
      </div>

      <div className={cn('flex items-center gap-1', !floating && 'mt-2')}>
        {!floating && <AssistantMark className="mr-auto" />}
        {variant === 'home' && onShowRecents && (
          <Button variant="ghost" size="sm" onClick={onShowRecents} className="gap-1 text-muted-foreground">
            <History /> {t.recents}
          </Button>
        )}
        {capabilities?.images && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t.attach}><Plus /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setImageOpen(true)}>
                <ImagePlus className="mr-2 size-4" /> {t.generateImage}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {capabilities?.voice && (
          <Button
            variant="ghost"
            size="icon"
            onClick={voice.toggle}
            disabled={voice.state === 'transcribing'}
            aria-label={t.voice}
            className={cn(voice.state === 'recording' && 'bg-violet-lighter text-violet-dark')}
          >
            {voice.state === 'transcribing' ? <Spinner className="size-4" /> : <Mic />}
          </Button>
        )}
        {floating && !text && !isBusy && (
          <Button variant="ghost" size="icon" onClick={isOpen ? close : open} aria-label={isOpen ? t.close : t.open}>
            <PanelRight />
          </Button>
        )}
        {(text || isBusy || !floating) && (
          isBusy ? (
            <Button size="icon" onClick={stop} aria-label={t.stop}><Square className="!size-3" /></Button>
          ) : (
            <Button size="icon" onClick={submit} disabled={!text.trim()} aria-label={t.send}><ArrowUp /></Button>
          )
        )}
      </div>
      <ImageDialog open={imageOpen} onOpenChange={setImageOpen} />
    </div>
  )
}
